import { useEffect, useState } from "react";
import { Modal, Button, Input, TextInput, FormError } from "@components";
import { useForm } from "react-hook-form";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

const PAGES = {
  MENU: "menu",
  NAME: "name",
  DELETE: "delete",
};

const SettingsMenuModal = ({
  isOpen,
  onClose,
  item,
  itemLabel = "item",
  onRename,
  onDelete,
  initialPage = PAGES.MENU,
  deleteWarning,
  isSerious = true,
}) => {
  const [page, setPage] = useState(initialPage);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm({ defaultValues: { name: item?.name ?? "" } });

  const isCreateMode = initialPage === PAGES.NAME && !item?.name;

  const handleClose = () => {
    setPage(initialPage);
    setConfirmText("");
    reset({ name: item?.name ?? "" });
    onClose();
  };

  const handleRename = handleSubmit(async (values) => {
    setLoading(true);
    try {
      await onRename(item, values.name);
      handleClose();
    } finally {
      setLoading(false);
    }
  });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(item);
      handleClose();
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setPage(initialPage);
      reset({ name: item?.name ?? "" });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && item && page === PAGES.MENU) {
      const currentName = getValues("name");
      if (currentName !== item.name) {
        reset({ name: item.name });
      }
    }
  }, [isOpen, item, page]);

  const titles = {
    [PAGES.MENU]: (
      <span className="flex items-center gap-2">
        <PencilSquareIcon className="size-4" /> {item?.name || itemLabel}
      </span>
    ),
    [PAGES.NAME]: isCreateMode
      ? `Add ${itemLabel}`
      : `Change ${itemLabel} name`,
    [PAGES.DELETE]: `Delete ${item?.name || itemLabel}`,
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={titles[page]}
      size="sm"
      preventBackdropClose
    >
      {page === PAGES.MENU && (
        <div className="p-5 space-y-3">
          <Button
            type="button"
            className="w-full rounded-lg justify-start"
            onClick={() => setPage(PAGES.NAME)}
          >
            Rename
          </Button>
          {!item?.isSystemDefault && onDelete && (
            <Button
              type="button"
              className="w-full rounded-lg justify-start text-red-500"
              onClick={() => {
                setConfirmText("");
                setPage(PAGES.DELETE);
              }}
            >
              Delete {itemLabel}
            </Button>
          )}
        </div>
      )}

      {page === PAGES.NAME && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleRename();
          }}
          className="p-5 space-y-4"
        >
          <TextInput
            name="name"
            control={control}
            placeholder={`Give your ${itemLabel} a name`}
            required
            errors={errors.name}
            displayName={`${itemLabel} name`}
            maxLength={50}
          />
          <FormError name="name" errors={errors} />
          <div className="flex gap-2">
            <Button
              type="button"
              className="shrink-0 rounded-lg"
              onClick={isCreateMode ? handleClose : () => setPage(PAGES.MENU)}
              disabled={loading}
            >
              {isCreateMode ? "Cancel" : "Back"}
            </Button>
            <Button
              type="submit"
              color="primary"
              className="shrink-0 grow rounded-lg"
              submitting={loading}
            >
              {isCreateMode ? "Add" : "Save"}
            </Button>
          </div>
        </form>
      )}

      {page === PAGES.DELETE && (
        <div className="flex flex-col max-h-[70vh]">
          <div className="shrink-0 px-5 pt-5 pb-2">
            <p className="text-sm text-zinc-600">
              You're about to permanently delete <strong>{item?.name}</strong>.
              {isSerious && " This action cannot be undone."}
            </p>
          </div>
          {deleteWarning && (
            <div className="flex-1 min-h-0 flex flex-col px-5 py-2">
              {deleteWarning}
            </div>
          )}
          <div className="shrink-0 px-5 pt-2 pb-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Type <strong>delete</strong> to continue
              </label>
              <Input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="delete"
                disabled={deleting}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                className="shrink-0 rounded-lg"
                onClick={() => setPage(PAGES.MENU)}
                disabled={deleting}
              >
                Back
              </Button>
              <Button
                type="button"
                color="danger"
                className="shrink-0 grow rounded-lg"
                onClick={handleDelete}
                disabled={deleting || confirmText.toLowerCase() !== "delete"}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SettingsMenuModal;
